import type { QueryClient } from '@tanstack/react-query';

// ============================================================================
// P-60 (D-236) — live updates without a refresh. There is no socket/SSE in the
// product, so ONE central polling policy lives here (applied once as QueryClient
// defaults in main.tsx) instead of per-page timers:
//   - open conversation  : 5s   (ticket, timeline, portal request messages)
//   - lists / counters   : 30s  (tickets, requests inbox, leads kanban, portal lists)
//   - every other query  : 60s  + refetch when the user returns to the tab
//     (heavy reports / previews: no interval, only the tab-return refetch - FIX-B38)
// TanStack Query already pauses intervals while the tab is hidden
// (refetchIntervalInBackground defaults to false), and structural sharing keeps
// unchanged data referentially equal, so an idle poll re-renders nothing.
// ============================================================================

export const LIVE_CONVERSATION_MS = 5_000;
export const LIVE_LIST_MS = 30_000;
export const LIVE_PAGE_MS = 60_000;

let pauseDepth = 0;

/** Holds all live polling (e.g. during a Kanban drag). Returns the release function (idempotent). */
export function pauseLiveUpdates(): () => void {
  pauseDepth += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    pauseDepth = Math.max(0, pauseDepth - 1);
  };
}

export function isLivePaused(): boolean {
  return pauseDepth > 0;
}

/** A react-query `refetchInterval` that is off while live updates are paused. */
export function liveInterval(ms: number): () => number | false {
  return () => (isLivePaused() ? false : ms);
}

const CONVERSATION_KEYS: ReadonlyArray<readonly string[]> = [
  ['ticket'],
  ['ticket-timeline'],
  ['portal', 'request-messages'],
];

const LIST_KEYS: ReadonlyArray<readonly string[]> = [
  ['tickets'],
  ['conversations'],
  ['conversations-grouped'],
  ['leads'],
  ['notifications'],
  ['portal', 'requests'],
  ['portal', 'request'],
  ['portal', 'conversations'],
  ['portal', 'conversation'],
  ['portal', 'ticket-approvals'],
  ['portal', 'ticket-approvals-count'],
  ['portal', 'pending-actions'],
  ['portal', 'recent-notifications'],
  // FIX-D251 §2 (D-236 §3): the portal project page carries "עובדים עכשיו על" (running timers),
  // a live status: with the lists (30s), not the 60s of a regular page.
  ['portal', 'project'],
];

/**
 * FIX-B38 (QX-842): heavy server computations (reports, profitability, billing /
 * consumption previews, AI units) get NO 60s interval - only the refetch when the
 * user returns to the tab. `dashboard-summary` keeps the 60s (D-236 §3).
 */
export function isHeavyQueryKey(key: readonly unknown[]): boolean {
  const head = key[0];
  if (typeof head !== 'string') return false;
  return head === 'reports' || head.startsWith('reports-')
    || head === 'profitability'
    || head === 'billing-report-preview'
    || head === 'consumption-preview'
    || head === 'ai-units' || head.startsWith('ai-units-');
}

/** The default page interval: 60s, none for heavy keys, none while paused. */
export function pageInterval(query: { queryKey: readonly unknown[] }): number | false {
  if (isLivePaused() || isHeavyQueryKey(query.queryKey)) return false;
  return LIVE_PAGE_MS;
}

/** FIX-B38 (QX-844): returning to the tab does not refetch while live updates are paused (a drag). */
export function liveWindowFocus(): boolean {
  return !isLivePaused();
}

/** Installs the central policy on a QueryClient (call once, before first render). */
export function applyLiveQueryDefaults(qc: QueryClient): void {
  qc.setDefaultOptions({
    queries: {
      ...qc.getDefaultOptions().queries,
      refetchOnWindowFocus: liveWindowFocus,
      refetchOnReconnect: true,
      staleTime: 15_000,
      refetchInterval: pageInterval,
    },
  });
  for (const key of LIST_KEYS) {
    qc.setQueryDefaults(key, { refetchInterval: liveInterval(LIVE_LIST_MS) });
  }
  for (const key of CONVERSATION_KEYS) {
    qc.setQueryDefaults(key, { refetchInterval: liveInterval(LIVE_CONVERSATION_MS), staleTime: 2_000 });
  }
}

// ---- "הודעה חדשה ↓" decision (pure, unit-tested) ---------------------------

export const NEAR_BOTTOM_PX = 80;

export function isNearBottom(
  m: { scrollTop: number; clientHeight: number; scrollHeight: number },
  threshold = NEAR_BOTTOM_PX,
): boolean {
  return m.scrollHeight - m.scrollTop - m.clientHeight <= threshold;
}

export interface NewItemsDecision {
  /** Scroll to the newest message. */
  autoScroll: boolean;
  /** Messages to add to the "הודעה חדשה ↓" counter (user is reading above). */
  addPending: number;
}

/** prevCount === null means the first render of the conversation. */
export function decideOnNewItems(args: {
  prevCount: number | null;
  nextCount: number;
  nearBottom: boolean;
  lastIsMine: boolean;
}): NewItemsDecision {
  const { prevCount, nextCount, nearBottom, lastIsMine } = args;
  if (prevCount === null) return { autoScroll: true, addPending: 0 };
  if (nextCount <= prevCount) return { autoScroll: false, addPending: 0 };
  if (lastIsMine || nearBottom) return { autoScroll: true, addPending: 0 };
  return { autoScroll: false, addPending: nextCount - prevCount };
}

/**
 * FIX-B38 (QX-840): the counter baseline of "הודעה חדשה ↓". When the conversation
 * changes (another ticket opened in the same view) the previous count means
 * nothing: start over as a first render, with no pending pill.
 */
export function pillBaseline(prevKey: unknown, key: unknown, prevCount: number | null): { prevCount: number | null; reset: boolean } {
  if (prevKey !== key) return { prevCount: null, reset: true };
  return { prevCount, reset: false };
}
